import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogIn, UserPlus, Globe, LayoutDashboard, Settings, LogOut, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

export default function LandingNavbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const { t, i18n } = useTranslation();
  const { currentUser, logout } = useAuth();
  const { setLanguage } = useTheme();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => item.href.substring(1)); // Remove # from href
      const scrollPosition = window.scrollY + 150; // Offset for navbar height

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    // Set initial active section
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const selectLanguage = (newLang) => {
    i18n.changeLanguage(newLang);
    setLanguage(newLang);
    document.documentElement.setAttribute('lang', newLang);
    document.documentElement.setAttribute('dir', newLang === 'ar' ? 'rtl' : 'ltr');
    setShowLangMenu(false);
  };

  const navItems = [
    { label: i18n.language === 'tr' ? 'Ana Sayfa' : isRTL ? 'الرئيسية' : 'Home', href: '#hero' },
    { label: i18n.language === 'tr' ? 'Özellikler' : isRTL ? 'المميزات' : 'Features', href: '#features' },
    { label: i18n.language === 'tr' ? 'Nasıl Çalışır' : isRTL ? 'كيف يعمل' : 'How It Works', href: '#how-it-works' },
    { label: i18n.language === 'tr' ? 'Fiyatlandırma' : isRTL ? 'الأسعار' : 'Pricing', href: '#pricing' },
    { label: i18n.language === 'tr' ? 'Ekip' : isRTL ? 'فريق العمل' : 'Team', href: '#team' },
    { label: i18n.language === 'tr' ? 'Bize Ulaşın' : isRTL ? 'تواصل معنا' : 'Contact Us', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    setIsMenuOpen(false);

    // If we're not on the home page, navigate there first
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          const headerOffset = 100; // Account for fixed navbar height
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) {
        const headerOffset = 100; // Account for fixed navbar height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      {/* Floating Navbar Container */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 lg:px-8 pt-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* Modern Pill-Shaped Navbar */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white/70 backdrop-blur-xl border border-slate-200/50 rounded-full shadow-lg shadow-slate-900/5"
          >
            <div className="flex justify-between items-center h-16 px-6 lg:px-8">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2 shrink-0" dir="ltr">
                <img 
                  src="/cvlogs-logo.png" 
                  alt="CVLogs" 
                  className="w-8 h-8 object-contain"
                />
                <span className="text-lg font-bold text-slate-900">Logs</span>
              </Link>

              {/* Desktop Navigation - Centered */}
              <div className="hidden lg:flex items-center justify-center gap-1 flex-1 mx-4">
                {navItems.map((item) => (
                  <button
                    key={item.href}
                    onClick={() => scrollToSection(item.href)}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                      activeSection === item.href.substring(1)
                        ? 'text-blue-600 bg-blue-50 shadow-sm'
                        : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100/80'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Desktop Actions */}
              <div className="hidden lg:flex items-center gap-3 shrink-0">
                <div className="relative">
                  <button
                    onClick={() => setShowLangMenu(!showLangMenu)}
                    className="p-2 rounded-full hover:bg-slate-100/80 transition-all duration-200"
                    title={i18n.language === 'en' ? 'English' : i18n.language === 'ar' ? 'العربية' : 'Türkçe'}
                  >
                    <Globe className="w-5 h-5 text-slate-600" />
                  </button>

                  <AnimatePresence>
                    {showLangMenu && (
                      <>
                        <div 
                          className="fixed inset-0 z-40"
                          onClick={() => setShowLangMenu(false)}
                        />
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute right-0 mt-2 w-32 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-slate-200/50 py-2 z-50 overflow-hidden"
                        >
                          <button
                            onClick={() => selectLanguage('en')}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${i18n.language === 'en' ? 'text-blue-600 font-medium bg-blue-50/50' : 'text-slate-700'}`}
                          >
                            English
                          </button>
                          <button
                            onClick={() => selectLanguage('ar')}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${i18n.language === 'ar' ? 'text-blue-600 font-medium bg-blue-50/50' : 'text-slate-700'}`}
                          >
                            العربية
                          </button>
                          <button
                            onClick={() => selectLanguage('tr')}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${i18n.language === 'tr' ? 'text-blue-600 font-medium bg-blue-50/50' : 'text-slate-700'}`}
                          >
                            Türkçe
                          </button>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
                
                {currentUser ? (
                  /* Logged In User Menu */
                  <div className="flex items-center gap-3">
                    <Link
                      to="/interview-prep"
                      className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white border border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 rounded-full transition-all duration-200 font-medium text-sm shadow-sm"
                    >
                      <User className="w-4 h-4" />
                      {i18n.language === 'tr' ? 'Mülakat Hazırlığı' : i18n.language === 'tr' ? 'Mülakat Hazırlığı' : isRTL ? 'محاكي المقابلات' : 'Interview Prep'}
                    </Link>

                    <Link
                      to="/dashboard"
                      className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-full transition-all duration-200 font-medium text-sm shadow-lg shadow-blue-600/20"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      {i18n.language === 'tr' ? 'Kontrol Paneli' : i18n.language === 'tr' ? 'Kontrol Paneli' : isRTL ? 'لوحة التحكم' : 'Dashboard'}
                    </Link>

                    <div className="relative">
                      <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100/80 transition-all duration-200"
                    >
                      {currentUser.photoURL ? (
                        <img
                          src={currentUser.photoURL}
                          alt={currentUser.displayName}
                          className="w-9 h-9 rounded-full border-2 border-blue-500"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white border-2 border-blue-500">
                          <User className="w-5 h-5" />
                        </div>
                      )}
                    </button>

                    <AnimatePresence>
                      {showUserMenu && (
                        <>
                          {/* Backdrop */}
                          <div 
                            className="fixed inset-0 z-40"
                            onClick={() => setShowUserMenu(false)}
                          />
                          
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-slate-200/50 py-2 z-50"
                          >
                            <div className="px-4 py-3 border-b border-slate-100">
                              <p className="font-semibold text-slate-900 truncate">{currentUser.displayName}</p>
                              <p className="text-sm text-slate-600 truncate">{currentUser.email}</p>
                            </div>
                            
                            <Link
                              to="/settings"
                              onClick={() => setShowUserMenu(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-100/80 transition-all duration-200 mx-2 rounded-xl"
                            >
                              <Settings className="w-4 h-4" />
                              <span className="text-sm font-medium">{i18n.language === 'tr' ? 'Ayarlar' : i18n.language === 'tr' ? 'Ayarlar' : isRTL ? 'الإعدادات' : 'Settings'}</span>
                            </Link>
                            
                            <div className="border-t border-slate-100 my-2"></div>
                            
                            <button
                              onClick={handleLogout}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-all duration-200 mx-2 rounded-xl mb-2"
                            >
                              <LogOut className="w-4 h-4" />
                              <span className="text-sm font-medium">{i18n.language === 'tr' ? 'Çıkış Yap' : i18n.language === 'tr' ? 'Çıkış Yap' : isRTL ? 'تسجيل الخروج' : 'Logout'}</span>
                            </button>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                  </div>
                ) : (
                  /* Guest User Buttons */
                  <div className="flex items-center gap-2">
                    <Link
                      to="/login"
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 rounded-full transition-all duration-200"
                    >
                      <LogIn className="w-4 h-4" />
                      {i18n.language === 'tr' ? 'Giriş Yap' : i18n.language === 'tr' ? 'Giriş Yap' : isRTL ? 'تسجيل الدخول' : 'Login'}
                    </Link>
                    
                    <Link
                      to="/register"
                      className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-full transition-all duration-200 font-medium text-sm shadow-lg shadow-blue-600/20"
                    >
                      <UserPlus className="w-4 h-4" />
                      {i18n.language === 'tr' ? 'Kayıt Ol' : i18n.language === 'tr' ? 'Kayıt Ol' : isRTL ? 'إنشاء حساب' : 'Sign Up'}
                    </Link>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 rounded-full hover:bg-slate-100/80 transition-all duration-200"
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6 text-slate-700" />
                ) : (
                  <Menu className="w-6 h-6 text-slate-700" />
                )}
              </button>
            </div>
          </motion.div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Mobile Menu Panel */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed top-24 left-4 right-4 z-50 lg:hidden"
            >
              <div className="bg-white/95 backdrop-blur-xl border border-slate-200/50 rounded-3xl shadow-2xl overflow-hidden">
                <div className="px-6 py-6 space-y-2 max-h-[calc(100vh-120px)] overflow-y-auto">
                  {navItems.map((item) => (
                    <button
                      key={item.href}
                      onClick={() => scrollToSection(item.href)}
                      className={`block w-full text-left px-4 py-3 rounded-2xl transition-all duration-200 font-medium ${
                        activeSection === item.href.substring(1)
                          ? 'text-blue-600 bg-blue-50 shadow-sm'
                          : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100/80'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                  
                  <div className="pt-4 border-t border-slate-200/50 space-y-2">
                    <div className="px-2">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">
                        {i18n.language === 'tr' ? 'Dil' : i18n.language === 'tr' ? 'Dil' : isRTL ? 'اللغة' : 'Language'}
                      </p>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => { selectLanguage('en'); setIsMenuOpen(false); }}
                          className={`flex items-center gap-3 w-full px-4 py-3 rounded-2xl transition-all duration-200 font-medium ${i18n.language === 'en' ? 'bg-blue-50 text-blue-600' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100/80'}`}
                        >
                          <Globe className="w-5 h-5" />
                          <span>English</span>
                        </button>
                        <button
                          onClick={() => { selectLanguage('ar'); setIsMenuOpen(false); }}
                          className={`flex items-center gap-3 w-full px-4 py-3 rounded-2xl transition-all duration-200 font-medium ${i18n.language === 'ar' ? 'bg-blue-50 text-blue-600' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100/80'}`}
                        >
                          <Globe className="w-5 h-5" />
                          <span>العربية</span>
                        </button>
                        <button
                          onClick={() => { selectLanguage('tr'); setIsMenuOpen(false); }}
                          className={`flex items-center gap-3 w-full px-4 py-3 rounded-2xl transition-all duration-200 font-medium ${i18n.language === 'tr' ? 'bg-blue-50 text-blue-600' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100/80'}`}
                        >
                          <Globe className="w-5 h-5" />
                          <span>Türkçe</span>
                        </button>
                      </div>
                    </div>
                    
                    {currentUser ? (
                      /* Logged In User - Mobile */
                      <>
                        <div className="px-4 py-4 bg-slate-50/80 rounded-2xl">
                          <div className="flex items-center gap-3">
                            {currentUser.photoURL ? (
                              <img
                                src={currentUser.photoURL}
                                alt={currentUser.displayName}
                                className="w-12 h-12 rounded-full border-2 border-blue-500"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white border-2 border-blue-500">
                                <User className="w-6 h-6" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-900 truncate">{currentUser.displayName}</p>
                              <p className="text-sm text-slate-600 truncate">{currentUser.email}</p>
                            </div>
                          </div>
                        </div>
                        
                        <Link
                          to="/dashboard"
                          className="flex items-center gap-3 w-full px-4 py-3 text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 rounded-2xl transition-all duration-200 font-medium"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <LayoutDashboard className="w-5 h-5" />
                          {i18n.language === 'tr' ? 'Kontrol Paneli' : i18n.language === 'tr' ? 'Kontrol Paneli' : isRTL ? 'لوحة التحكم' : 'Dashboard'}
                        </Link>
                        
                        <Link
                          to="/interview-prep"
                          className="flex items-center gap-3 w-full px-4 py-3 text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 rounded-2xl transition-all duration-200 font-medium"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <User className="w-5 h-5" />
                          {i18n.language === 'tr' ? 'Mülakat Simülatörü' : i18n.language === 'tr' ? 'Mülakat Simülatörü' : isRTL ? 'محاكي المقابلات' : 'Interview Simulator'}
                        </Link>
                        
                        <Link
                          to="/settings"
                          className="flex items-center gap-3 w-full px-4 py-3 text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 rounded-2xl transition-all duration-200 font-medium"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <Settings className="w-5 h-5" />
                          {i18n.language === 'tr' ? 'Ayarlar' : i18n.language === 'tr' ? 'Ayarlar' : isRTL ? 'الإعدادات' : 'Settings'}
                        </Link>
                        
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-2xl transition-all duration-200 font-medium"
                        >
                          <LogOut className="w-5 h-5" />
                          {i18n.language === 'tr' ? 'Çıkış Yap' : i18n.language === 'tr' ? 'Çıkış Yap' : isRTL ? 'تسجيل الخروج' : 'Logout'}
                        </button>
                      </>
                    ) : (
                      /* Guest User - Mobile */
                      <>
                        <Link
                          to="/login"
                          className="flex items-center gap-3 w-full px-4 py-3 text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 rounded-2xl transition-all duration-200 font-medium"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <LogIn className="w-5 h-5" />
                          {i18n.language === 'tr' ? 'Giriş Yap' : i18n.language === 'tr' ? 'Giriş Yap' : isRTL ? 'تسجيل الدخول' : 'Login'}
                        </Link>
                        
                        <Link
                          to="/register"
                          className="flex items-center gap-3 w-full px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 rounded-2xl transition-all duration-200 font-medium shadow-lg shadow-blue-600/20"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <UserPlus className="w-5 h-5" />
                          {i18n.language === 'tr' ? 'Kayıt Ol' : i18n.language === 'tr' ? 'Kayıt Ol' : isRTL ? 'إنشاء حساب' : 'Sign Up'}
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
